import React from "react"
import { useRouter } from "../hooks/use-router"
import { AppMainContextType, AppSubContextType } from "../types/app-context"
import { ContextManager } from "./context-manager"
import { useAppDispatch, useAppSelector } from "../store"
import { buildPathWithContext } from "./navigate"
import { useFetchContextOptionsQuery } from "../context-options/api"
import { getContextOptions } from "../context-options/store/selectors"
import { SetContextOption } from "../context-options/store"

export const useAppContext = () => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.auth)
  // In the future, we might want to remove context options from the store and
  // rely solely on the server response (RTK Query), but for now we will keep it in the store.
  const contextOptions = useAppSelector(getContextOptions)
  const { data, isSuccess } = useFetchContextOptionsQuery(user?.id ?? "", {
    skip: !user?.id,
  })
  const { push, location } = router
  const query = router.query as { mainContext?: string; subContext?: string }
  const contextManager = React.useMemo(() => new ContextManager(contextOptions), [contextOptions])
  const appContext = contextManager.deriveContextFromQuery(query)

  // Sync context options from store with server
  React.useEffect(() => {
    if (!isSuccess || !data) return

    data.forEach((item) => {
      dispatch(SetContextOption(item))
    })
  }, [isSuccess, data, dispatch])

  React.useEffect(() => {
    if (!query.mainContext) {
      const defaultMainContext = contextManager.getDefaultMainContext()
      const defaultSubContext = contextManager.getOwnMemberContext(defaultMainContext.id)
      const nextContext = defaultSubContext || defaultMainContext
      const nextPath = buildPathWithContext({
        currentPath: location.pathname,
        currentSearch: location.search,
        nextContext,
      })
      push(nextPath)
    }
  }, [query.mainContext, location.pathname, location.search, push, contextManager])

  function onChangeMainContext(nextMainContext: AppMainContextType) {
    const currentContext = appContext.subContext || appContext.mainContext
    const nextContext =
      nextMainContext.type === "org" ? contextManager.getOwnMemberContext(nextMainContext.id) : nextMainContext

    if (!nextContext || !currentContext) return

    const nextPath = buildPathWithContext({
      currentPath: location.pathname,
      currentSearch: location.search,
      currentContext,
      nextContext,
    })
    push(nextPath)
  }

  function onChangeSubContext(subContext: AppSubContextType | null) {
    const currentContext = appContext.subContext || appContext.mainContext
    const nextContext = subContext || appContext.mainContext
    if (!nextContext || !currentContext) return
    const nextPath = buildPathWithContext({
      currentPath: location.pathname,
      currentSearch: location.search,
      currentContext,
      nextContext,
    })
    push(nextPath)
  }

  return {
    appContext,
    onChangeMainContext,
    onChangeSubContext,
  }
}
